import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    CircularProgress,
    Alert,
    TextField,
    IconButton,
    MenuItem,
    Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../api/axios';

interface MaxAccount {
    _id: string;
    fullName: string;
    maxUserId: string;
    username: string;
    role: 'ADMIN' | 'CUSTOMER';
    email: string;
    phone: string;
    isActive: boolean;
}

const MaxAccountsPage = () => {
    const [accounts, setAccounts] = useState<MaxAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [newAccount, setNewAccount] = useState({
        fullName: '',
        maxUserId: '',
        username: '',
        role: 'ADMIN' as 'ADMIN' | 'CUSTOMER',
        email: '',
        phone: '',
    });

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/admin/max-accounts');
            setAccounts(response.data);
        } catch (err) {
            console.error(err);
            setError('Ошибка при загрузке MAX аккаунтов.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    const handleAddAccount = async () => {
        if (!newAccount.fullName || !newAccount.maxUserId) {
            setError('Введите ФИО и MAX User ID.');
            return;
        }

        try {
            const response = await api.post('/api/admin/max-accounts', newAccount);
            setAccounts((prev) => [response.data, ...prev]);
            setNewAccount({
                fullName: '',
                maxUserId: '',
                username: '',
                role: 'ADMIN',
                email: '',
                phone: '',
            });
            setError('');
        } catch (err) {
            console.error(err);
            setError('Не удалось добавить MAX аккаунт.');
        }
    };

    const handleUpdateAccount = async (
        id: string,
        field: keyof MaxAccount,
        value: string | boolean
    ) => {
        try {
            setAccounts((prev) =>
                prev.map((acc) => (acc._id === id ? { ...acc, [field]: value } : acc))
            );

            await api.put(`/api/admin/max-accounts/${id}`, {
                [field]: value,
            });
        } catch (err) {
            console.error(err);
            setError('Ошибка при обновлении MAX аккаунта.');
            fetchAccounts();
        }
    };

    const handleDeleteAccount = async (id: string) => {
        if (!window.confirm('Удалить этот MAX аккаунт?')) return;

        try {
            await api.delete(`/api/admin/max-accounts/${id}`);
            setAccounts((prev) => prev.filter((acc) => acc._id !== id));
        } catch (err) {
            console.error(err);
            setError('Не удалось удалить MAX аккаунт.');
        }
    };

    return (
        <Box p={4}>
            <Typography variant="h5" mb={3}>
                Реестр MAX аккаунтов
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box display="grid" gridTemplateColumns="repeat(6, 1fr)" gap={2} mb={3}>
                <TextField
                    label="ФИО"
                    value={newAccount.fullName}
                    onChange={(e) => setNewAccount({ ...newAccount, fullName: e.target.value })}
                />
                <TextField
                    label="MAX User ID"
                    value={newAccount.maxUserId}
                    onChange={(e) => setNewAccount({ ...newAccount, maxUserId: e.target.value })}
                />
                <TextField
                    label="Username"
                    value={newAccount.username}
                    onChange={(e) => setNewAccount({ ...newAccount, username: e.target.value })}
                />
                <TextField
                    select
                    label="Роль"
                    value={newAccount.role}
                    onChange={(e) =>
                        setNewAccount({
                            ...newAccount,
                            role: e.target.value as 'ADMIN' | 'CUSTOMER',
                        })
                    }
                >
                    <MenuItem value="ADMIN">ADMIN</MenuItem>
                    <MenuItem value="CUSTOMER">CUSTOMER</MenuItem>
                </TextField>
                <TextField
                    label="Email"
                    value={newAccount.email}
                    onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })}
                />
                <TextField
                    label="Телефон"
                    value={newAccount.phone}
                    onChange={(e) => setNewAccount({ ...newAccount, phone: e.target.value })}
                />

                <Box gridColumn="1 / -1">
                    <Button variant="contained" onClick={handleAddAccount}>
                        Добавить
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : accounts.length === 0 ? (
                <Typography>Список аккаунтов пуст.</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>№</TableCell>
                                <TableCell>ФИО</TableCell>
                                <TableCell>MAX User ID</TableCell>
                                <TableCell>Username</TableCell>
                                <TableCell>Роль</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Телефон</TableCell>
                                <TableCell>Активен</TableCell>
                                <TableCell>Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {accounts.map((acc, index) => (
                                <TableRow key={acc._id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="standard"
                                            value={acc.fullName}
                                            onChange={(e) =>
                                                handleUpdateAccount(acc._id, 'fullName', e.target.value)
                                            }
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="standard"
                                            value={acc.maxUserId}
                                            onChange={(e) =>
                                                handleUpdateAccount(acc._id, 'maxUserId', e.target.value)
                                            }
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="standard"
                                            value={acc.username || ''}
                                            onChange={(e) =>
                                                handleUpdateAccount(acc._id, 'username', e.target.value)
                                            }
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            select
                                            variant="standard"
                                            value={acc.role}
                                            onChange={(e) =>
                                                handleUpdateAccount(acc._id, 'role', e.target.value)
                                            }
                                            fullWidth
                                        >
                                            <MenuItem value="ADMIN">ADMIN</MenuItem>
                                            <MenuItem value="CUSTOMER">CUSTOMER</MenuItem>
                                        </TextField>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="standard"
                                            value={acc.email || ''}
                                            onChange={(e) =>
                                                handleUpdateAccount(acc._id, 'email', e.target.value)
                                            }
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="standard"
                                            value={acc.phone || ''}
                                            onChange={(e) =>
                                                handleUpdateAccount(acc._id, 'phone', e.target.value)
                                            }
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Checkbox
                                            checked={Boolean(acc.isActive)}
                                            onChange={(e) =>
                                                handleUpdateAccount(acc._id, 'isActive', e.target.checked)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton color="error" onClick={() => handleDeleteAccount(acc._id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default MaxAccountsPage;